import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { funilApi, type PlanoItem } from '@/api/funil';
import { classesDeBotao } from '@/components/atoms';
import { contatoDeVendas } from '@/config/vendas';

import styles from './Planos.module.css';

function formatarValorMoeda(valor: string, moeda: 'BRL' | 'USD', locale: string): string {
  const num = Number(valor);
  if (isNaN(num)) {
    return valor;
  }
  return new Intl.NumberFormat(locale === 'en-US' ? 'en-US' : 'pt-BR', {
    style: 'currency',
    currency: moeda,
  }).format(num);
}

export function Planos() {
  const { t, i18n } = useTranslation(['funil', 'common']);
  const vendas = contatoDeVendas();

  const [planos, setPlanos] = useState<PlanoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function carregarPlanos() {
    setLoading(true);
    setError(null);
    try {
      const res = await funilApi.getPlanos();
      setPlanos(res.planos || []);
    } catch {
      setError(t('funil:planos.erro_carregar'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void carregarPlanos();
  }, []);

  // A compra é atendida por uma pessoa: o link leva o plano escolhido junto, para
  // que quem atende já saiba do que se trata sem uma rodada de perguntas.
  function linkDeVendas(plano: PlanoItem): string | null {
    if (!vendas) return null;
    const assunto = t('funil:planos.vendas_assunto', 'Interesse no plano {{plano}}', {
      plano: plano.nome,
    });
    if (vendas.startsWith('mailto:')) {
      return `${vendas}?subject=${encodeURIComponent(assunto)}`;
    }
    if (vendas.startsWith('https://wa.me/')) {
      return `${vendas}?text=${encodeURIComponent(assunto)}`;
    }
    return vendas;
  }

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('funil:planos.titulo')}</h1>
          <p className={styles.subtitle}>{t('funil:planos.subtitulo')}</p>
        </div>

        {loading && <div className={styles.loading}>{t('funil:planos.carregando')}</div>}

        {error && !loading && (
          <div className={styles.errorCard}>
            <p>{error}</p>
            <button type="button" className={styles.retryBtn} onClick={() => void carregarPlanos()}>
              {t('funil:planos.tentar_novamente')}
            </button>
          </div>
        )}

        {!loading && !error && planos.length === 0 && (
          <div className={styles.emptyCard}>
            <h2>{t('funil:planos.vazio_titulo')}</h2>
            <p>{t('funil:planos.vazio_descricao')}</p>
          </div>
        )}

        {!loading && !error && planos.length > 0 && (
          <div className={styles.grid}>
            {planos.map((plano) => (
              <div key={plano.id} className={plano.destaque ? styles.cardDestaque : styles.card}>
                {plano.destaque && (
                  <span className={styles.badge}>{t('funil:planos.destaque_badge')}</span>
                )}
                <h2 className={styles.planName}>{plano.nome}</h2>
                <div className={styles.priceWrapper}>
                  <span className={styles.priceValue}>
                    {formatarValorMoeda(plano.preco.valor, plano.preco.moeda, i18n.language)}
                  </span>
                  <span className={styles.pricePeriod}>{t('funil:planos.periodo_mensal')}</span>
                </div>
                {plano.resumo && <p className={styles.planResumo}>{plano.resumo}</p>}
                <ul className={styles.featureList}>
                  {plano.features.map((feature, idx) => (
                    <li key={idx} className={styles.featureItem}>
                      <span className={styles.checkIcon}>✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {linkDeVendas(plano) ? (
                  // Link com cara de botão, e não <Button> — quem clica aqui vai
                  // para fora do app (cliente de e-mail, WhatsApp) e espera poder
                  // abrir em aba nova e copiar o endereço. `classesDeBotao` existe
                  // no repo justamente para isto.
                  <a
                    href={linkDeVendas(plano) as string}
                    className={classesDeBotao(
                      plano.destaque ? 'primary' : 'secondary',
                      'md',
                      styles.selectBtn,
                    )}
                  >
                    {t('funil:planos.botao_vendas', 'Falar com vendas')}
                  </a>
                ) : (
                  // Sem canal configurado, o cartão diz a verdade em vez de
                  // oferecer um botão que não leva a lugar nenhum — e o plano
                  // continua visível, porque ele existe.
                  <p className={styles.semCanal}>
                    {t('funil:planos.vendas_sem_canal', 'Contratação pelo time comercial.')}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
